// Pass 2.12 — viewport-width threshold hook.
//
// Returns true when window.innerWidth >= threshold. Used by
// BrowseView to gate the resize handles: below 900px, the panes
// fall back to fixed default widths (no handles, no overflow
// risk) because (a) the handles take a 4px slice of an already-
// narrow viewport and (b) touch devices that report narrow
// viewports often handle pointer-drag flakily.
//
// Server-side: defaults to true so the SSR snapshot doesn't
// hide the handles. Subsequent client mount will fix it on
// first paint.

import { useEffect, useState } from "react";

export function useViewportThreshold(threshold: number): boolean {
  const [isAbove, setIsAbove] = useState<boolean>(() => {
    if (typeof window === "undefined") return true;
    return window.innerWidth >= threshold;
  });

  useEffect(() => {
    if (typeof window === "undefined") return;
    const onResize = (): void => {
      setIsAbove(window.innerWidth >= threshold);
    };
    // Always re-evaluate at mount time too, so a hot-reload that
    // skipped initialization runs the same path.
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [threshold]);

  return isAbove;
}
